import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import React from 'react'

import Button from '@/app/component/atom/button'
import Dialog from '@/app/component/atom/dialog'
import DialogHeader from '@/app/component/atom/dialog/header'
import { Checkbox } from '@/app/component/atom/form'
import CampaignSelect from '@/app/module/campaigns/component/campaign-select'
import Delay from '@/app/module/campaigns/component/subscriptions/delay'
import { Q_ACTIONS } from '@/app/module/campaigns/definitions'
import { DelayState, LocalSubscribeToCampaignAction, TimeUnit } from '@/app/module/campaigns/types'
import { toTimezoneOptions } from '@/app/service/util/timezone'
import { CountryTimezone, SelectOption, TimezoneWithCountryCodeType } from '@/app/types'

type Props = {
  countriesTimezones: CountryTimezone[]
  countryCode: string
  timezones: TimezoneWithCountryCodeType
  timezone: string
  value: LocalSubscribeToCampaignAction

  onClose: () => void
  onSave: (value: Pick<LocalSubscribeToCampaignAction, 'subscribeToCampaign'>) => void
}

const SubscribeToCampaign: React.FC<Props> = (props) => {
  const { countriesTimezones, countryCode, timezone, timezones, onClose, onSave } = props

  const {
    campaignId: initialCampaignId,
    campaignName,
    allowResubscribe: initialAllowResubscribe,
    delay: initialDelay,
  } = unwrapValue(props.value)
  const [selectedCampaign, setSelectedCampaign] = React.useState<SelectOption<number>>({
    label: campaignName || `Campaign ${initialCampaignId}`,
    value: initialCampaignId,
  })
  const campaignId = selectedCampaign.value
  const country = initialDelay?.country || countryCode
  const defaultDelay = {
    value: 0,
    dayOfWeek: '',
    daysOfWeek: {},
    time: '',
    timezone,
    timezones: toTimezoneOptions(timezones[country]?.tzs ?? []),
    country,
    at: undefined,
    valueError: '',
    ...initialDelay,
    unit: initialDelay?.unit ? (initialDelay.unit as TimeUnit) : TimeUnit.Immediate,
  }

  const [delay, setDelay] = React.useState<DelayState>(defaultDelay)
  const [allowResubscribe, setAllowResubscribe] = React.useState<boolean>(
    typeof initialAllowResubscribe === 'boolean' ? initialAllowResubscribe : true, // default to true if undefined
  )

  return (
    <Dialog open onClose={onClose}>
      <DialogHeader
        closeButtonLabel="Close"
        icon={Q_ACTIONS.subscribeToCampaign.icon}
        title={Q_ACTIONS.subscribeToCampaign.label}
        onClose={onClose}
      />
      <DialogContent>
        <div
          style={{
            width: '80vw',
            minWidth: '300px',
            maxWidth: '100%',
          }}
        >
          <form
            id="send-message-form"
            onSubmit={(e) => {
              e.preventDefault()
            }}
          >
            <div className="subscribe-contact-campaign-select">
              <CampaignSelect
                selectedCampaignName={selectedCampaign.label}
                value={campaignId}
                onChange={(selectedId, selectedName) => {
                  setSelectedCampaign({ label: selectedName, value: Number(selectedId) })
                }}
              />
            </div>
            <SectionDivider />
            <div className="subscribe-contact-delay">
              <Typography color="primary" variant="subtitle2">
                When should the subscription start?
              </Typography>
              <Delay
                isFirst={true}
                published={false}
                item={delay}
                changeHandler={setDelay}
                timezones={timezones}
                countriesTimezones={countriesTimezones}
                setLoading={() => {}}
                type="message"
                text=""
              />
            </div>
            <SectionDivider />
            <div className="subscribe-contact-resubscribe">
              <Typography color="primary" variant="subtitle2">
                Resubscription
              </Typography>
              <Typography color="textSecondary" variant="caption">
                Once a contact is subscribed to the selected campaign, they will not be subscribed again by this action,
                unless you check the box below.
              </Typography>
              <div
                style={{
                  marginTop: '15px',
                }}
              >
                <Checkbox
                  label="Allow resubscribe (allow a contact to be subscribed to the selected campaign again by this action)"
                  name="allow-resubscribe"
                  value={allowResubscribe ? 'true' : 'false'}
                  editable={true}
                  onChange={({ value }) => {
                    setAllowResubscribe(value === 'true')
                  }}
                />
              </div>
            </div>
          </form>
        </div>
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          color="primary"
          disabled={!campaignId}
          onClick={() => {
            const { timezones: _timezones, valueError: _valueError, ...subscriptionDelay } = delay
            onSave({
              subscribeToCampaign: {
                campaignId,
                campaignName: selectedCampaign.label,
                allowResubscribe,
                ...(delay.unit !== 'immediate' && {
                  delay: subscriptionDelay,
                }),
              },
            })
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const SectionDivider = () => (
  <Divider
    style={{
      marginLeft: 0,
      marginRight: 0,
      marginTop: 15,
      marginBottom: 15,
    }}
  />
)

const unwrapValue = (value: LocalSubscribeToCampaignAction): LocalSubscribeToCampaignAction['subscribeToCampaign'] =>
  'subscribeToCampaign' in value
    ? value.subscribeToCampaign
    : {
        campaignId: 0,
        campaignName: '',
        allowResubscribe: true,
      }

export default SubscribeToCampaign

export type { Props as SubscribeToCampaignProps }
